"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { usePathname } from "next/navigation";
import {
  registerLoadingBridge,
  type LoadingBridgeApi,
  type ProgressStep,
  type ProgressStepStatus,
  type StartProgressLoadingConfig,
} from "@/lib/loadingBridge";

export type { ProgressStep, ProgressStepStatus, StartProgressLoadingConfig };

type LoadingMode = "overlay" | "progress";

type LoadingContextValue = {
  isLoading: boolean;
  loadingMessage: string;
  loadingMode: LoadingMode;
  progressTitle: string;
  progressValue: number;
  progressSteps: ProgressStep[];
  progressValueManual: boolean;
  startLoading: (message?: string) => void;
  stopLoading: () => void;
  setLoadingMessage: (message: string) => void;
  startProgressLoading: (config: StartProgressLoadingConfig) => void;
  updateProgressStep: (stepKey: string, status: ProgressStepStatus) => void;
  setProgressValue: (value: number) => void;
  /** Call from LoadingLink before navigation */
  beginNavigationLoading: () => void;
};

const LoadingContext = createContext<LoadingContextValue | null>(null);

const MIN_ROUTE_MS = 140;

function computeProgressFromSteps(steps: ProgressStep[]): number {
  if (steps.length === 0) return 0;
  const weights = { pending: 0, active: 0.5, completed: 1, error: 1 } as const;
  let sum = 0;
  for (const s of steps) {
    sum += weights[s.status];
  }
  return Math.min(100, Math.round((sum / steps.length) * 100));
}

export function LoadingProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const depthRef = useRef(0);
  const routePendingRef = useRef(false);
  const routeStartedAtRef = useRef(0);
  const progressManualRef = useRef(false);

  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessageState] = useState("Loading...");
  const [loadingMode, setLoadingMode] = useState<LoadingMode>("overlay");
  const [progressTitle, setProgressTitle] = useState("");
  const [progressValue, setProgressValueState] = useState(0);
  const [progressSteps, setProgressSteps] = useState<ProgressStep[]>([]);
  const [progressValueManual, setProgressValueManualFlag] = useState(false);

  const bumpLoading = useCallback(() => {
    depthRef.current += 1;
    if (depthRef.current === 1) {
      setIsLoading(true);
    }
  }, []);

  const releaseLoading = useCallback(() => {
    depthRef.current = Math.max(0, depthRef.current - 1);
    if (depthRef.current === 0) {
      setIsLoading(false);
      setLoadingMode("overlay");
      setProgressTitle("");
      setProgressSteps([]);
      setProgressValueState(0);
      progressManualRef.current = false;
      setProgressValueManualFlag(false);
      setLoadingMessageState("Loading...");
    }
  }, []);

  const startLoading = useCallback(
    (message = "Loading...") => {
      setLoadingMode("overlay");
      progressManualRef.current = false;
      setProgressValueManualFlag(false);
      setProgressSteps([]);
      setProgressTitle("");
      setProgressValueState(0);
      setLoadingMessageState(message);
      bumpLoading();
    },
    [bumpLoading],
  );

  const stopLoading = useCallback(() => {
    releaseLoading();
  }, [releaseLoading]);

  const setLoadingMessage = useCallback((message: string) => {
    setLoadingMessageState(message);
  }, []);

  const setProgressValue = useCallback((value: number) => {
    progressManualRef.current = true;
    setProgressValueManualFlag(true);
    setProgressValueState(Math.min(100, Math.max(0, value)));
  }, []);

  const startProgressLoading = useCallback(
    (config: StartProgressLoadingConfig) => {
      setLoadingMode("progress");
      progressManualRef.current = false;
      setProgressValueManualFlag(false);
      setProgressTitle(config.title);
      const steps = config.steps.map((s) => ({ ...s }));
      setProgressSteps(steps);
      setLoadingMessageState(config.loadingMessage ?? steps.find((s) => s.status === "active")?.label ?? steps[0]?.label ?? "Working…");
      setProgressValueState(computeProgressFromSteps(steps));
      bumpLoading();
    },
    [bumpLoading],
  );

  const updateProgressStep = useCallback((stepKey: string, status: ProgressStepStatus) => {
    setProgressSteps((prev) => {
      let next = prev.map((s) => {
        if (s.key !== stepKey) return s;
        return { ...s, status };
      });
      if (status === "active") {
        next = next.map((s) =>
          s.key !== stepKey && s.status === "active" ? { ...s, status: "pending" as const } : s,
        );
      }
      if (!progressManualRef.current) {
        setProgressValueState(computeProgressFromSteps(next));
      }
      return next;
    });
  }, []);

  const beginNavigationLoading = useCallback(() => {
    routePendingRef.current = true;
    routeStartedAtRef.current = Date.now();
    startLoading("Loading page...");
  }, [startLoading]);

  useEffect(() => {
    if (!routePendingRef.current) return;
    const elapsed = Date.now() - routeStartedAtRef.current;
    const wait = Math.max(0, MIN_ROUTE_MS - elapsed);
    const t = window.setTimeout(() => {
      routePendingRef.current = false;
      stopLoading();
    }, wait);
    return () => window.clearTimeout(t);
  }, [pathname, stopLoading]);

  useEffect(() => {
    if (loadingMode !== "progress" || progressSteps.length === 0) return;
    const active = progressSteps.find((s) => s.status === "active");
    const err = progressSteps.find((s) => s.status === "error");
    if (err) {
      setLoadingMessageState(err.label);
      return;
    }
    if (active) setLoadingMessageState(active.label);
  }, [progressSteps, loadingMode]);

  const value = useMemo<LoadingContextValue>(
    () => ({
      isLoading,
      loadingMessage,
      loadingMode,
      progressTitle,
      progressValue,
      progressSteps,
      progressValueManual,
      startLoading,
      stopLoading,
      setLoadingMessage,
      startProgressLoading,
      updateProgressStep,
      setProgressValue,
      beginNavigationLoading,
    }),
    [
      isLoading,
      loadingMessage,
      loadingMode,
      progressTitle,
      progressValue,
      progressSteps,
      progressValueManual,
      startLoading,
      stopLoading,
      setLoadingMessage,
      startProgressLoading,
      updateProgressStep,
      setProgressValue,
      beginNavigationLoading,
    ],
  );

  useEffect(() => {
    const api: LoadingBridgeApi = {
      startLoading,
      stopLoading,
      setLoadingMessage,
      startProgressLoading,
      updateProgressStep,
      setProgressValue,
      beginNavigationLoading,
    };
    registerLoadingBridge(api);
    return () => registerLoadingBridge(null);
  }, [
    startLoading,
    stopLoading,
    setLoadingMessage,
    startProgressLoading,
    updateProgressStep,
    setProgressValue,
    beginNavigationLoading,
  ]);

  return <LoadingContext.Provider value={value}>{children}</LoadingContext.Provider>;
}

export function useLoading() {
  const ctx = useContext(LoadingContext);
  if (!ctx) {
    throw new Error("useLoading must be used within LoadingProvider");
  }
  return ctx;
}

/** Safe for optional use outside provider (no-op). */
export function useLoadingOptional() {
  return useContext(LoadingContext);
}
